import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Bell, BellOff, Send, CheckCircle, XCircle, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import usePushNotifications from "@/hooks/usePushNotifications";

const NotificationSettings = ({ studentId, studentName }) => {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications(studentId);

  const [testSending, setTestSending] = useState(false);

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success("Notifications désactivées");
      } else {
        toast.error("Erreur lors de la désactivation");
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success("Notifications activées ! Les philosophes pourront vous contacter.");
      } else if (permission === 'denied') {
        toast.error("Vous avez bloqué les notifications. Modifiez les paramètres de votre navigateur.");
      } else {
        toast.error("Erreur lors de l'activation des notifications");
      }
    }
  };

  const handleTestNotification = async () => {
    setTestSending(true);
    try {
      const result = await sendTestNotification();
      if (result.status === 'sent') {
        toast.success("Notification de test envoyée !");
      } else {
        toast.error("Échec de l'envoi");
      }
    } catch (err) {
      toast.error("Erreur lors de l'envoi du test");
    } finally {
      setTestSending(false);
    }
  };

  if (!isSupported) {
    return (
      <Card className="border-[#E2E8F0]">
        <CardHeader>
          <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2">
            <BellOff className="w-5 h-5 text-[#64748B]" />
            Notifications non disponibles
          </CardTitle>
          <CardDescription>
            Votre navigateur ne supporte pas les notifications push.
            Essayez d'utiliser Chrome, Firefox ou Edge.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="border-[#D4AF37]/30 bg-gradient-to-r from-[#FFFBEB] to-white" data-testid="notification-settings">
      <CardHeader>
        <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Bell className="w-5 h-5 text-[#D4AF37]" />
          Notifications Philosophiques
        </CardTitle>
        <CardDescription>
          Recevez des messages de vos philosophes préférés pour vous motiver
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Toggle */}
        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[#E2E8F0]">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSubscribed ? 'bg-[#22C55E]/10' : 'bg-[#64748B]/10'}`}>
              {isSubscribed ? (
                <Bell className="w-5 h-5 text-[#22C55E]" />
              ) : (
                <BellOff className="w-5 h-5 text-[#64748B]" />
              )}
            </div>
            <div>
              <p className="font-medium text-[#0F172A]">Recevoir les notifications</p>
              <p className="text-sm text-[#64748B]">
                {isSubscribed 
                  ? "Les philosophes peuvent vous envoyer des messages" 
                  : "Activez pour recevoir des rappels philosophiques"}
              </p>
            </div>
          </div>
          <Switch
            checked={isSubscribed}
            onCheckedChange={handleToggle}
            disabled={loading}
          />
        </div>

        {/* Status */}
        <div className="flex items-center gap-2">
          {permission === 'granted' ? (
            <Badge className="bg-[#22C55E]/10 text-[#22C55E] border-[#22C55E]/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              Permission accordée
            </Badge>
          ) : permission === 'denied' ? (
            <Badge className="bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30">
              <XCircle className="w-3 h-3 mr-1" />
              Permission refusée
            </Badge>
          ) : (
            <Badge className="bg-[#64748B]/10 text-[#64748B] border-[#64748B]/30">
              <Smartphone className="w-3 h-3 mr-1" />
              En attente de permission
            </Badge>
          )}
          
          {isSubscribed && (
            <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30">
              <Bell className="w-3 h-3 mr-1" />
              Actif
            </Badge>
          )}
        </div>

        {/* Test Button */}
        {isSubscribed && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestNotification}
            disabled={testSending}
            className="w-full border-[#1A365D] text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
          >
            {testSending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Tester les notifications
              </>
            )}
          </Button>
        )}

        {/* Info */}
        <div className="text-xs text-[#64748B] space-y-1">
          <p>📚 <strong>Rappels d'inactivité</strong> : Un philosophe vous contacte après 2 jours sans révision</p>
          <p>🎯 <strong>Défis quotidiens</strong> : Une question philosophique pour stimuler votre réflexion</p>
          <p>🎉 <strong>Encouragements</strong> : Félicitations après un bon exercice</p>
          <p className="mt-2 italic">Les notifications sont envoyées après les cours en semaine (17h-18h) et le matin le week-end (10h-12h).</p>
        </div>

        {/* Error display */}
        {error && (
          <div className="p-3 bg-[#EF4444]/10 rounded-lg text-sm text-[#EF4444]">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
